/**
 * The stage table — State → Transition → Animation → Interaction → Next State.
 *
 * This is the engine loop, declared as data. Each state says what plays over it, how
 * long it must dwell, what the visitor may do, and where it goes next. The driver in
 * main.ts reads this; it contains no per-state branching of its own.
 *
 * Assets are OPTIONAL DECORATION at every stage. A missing asset skips its animation
 * and the journey continues — so the application boots and runs end to end today, and
 * gets richer as production media lands. That is the difference between an asset
 * library and an engine.
 */
import type { V1State } from './machine';
import type { AssetAlias } from './assets';

export interface Stage {
  /** Clip that plays over this state. Absent or unavailable → stage runs without it. */
  readonly animation?: AssetAlias;
  /** Minimum time in this state, ms. Used for the arrival beat, not for padding work. */
  readonly dwellMs?: number;
  /** What the visitor may do here. Nothing else is live (ENG-037). */
  readonly interactions: readonly ('drop' | 'knob' | 'transport')[];
  /** Automatic successor once the animation and dwell are satisfied. */
  readonly next?: V1State;
  /** True when the state ends only on a visitor action or an async result. */
  readonly awaits?: 'signal' | 'decode';
}

export const STAGES: Record<V1State, Stage> = {
  BOOTING: {
    interactions: [],
    next: 'STARTUP',
  },
  STARTUP: {
    animation: 'startup_transition_1080p',
    interactions: [],
    next: 'ARRIVED',
  },
  // The arrival beat: the workstation sits alive before anything is asked of the
  // visitor. Dwell is a deliberate experience choice, not manufactured loading time.
  ARRIVED: {
    dwellMs: 2600,
    interactions: [],
    next: 'IDLE_PROMPT',
  },
  IDLE_PROMPT: {
    interactions: ['drop'],
    awaits: 'signal',
  },
  ACCEPTING: {
    interactions: [],
    awaits: 'decode',
    next: 'WAKING',
  },
  WAKING: {
    animation: 'crt_power_bloom',
    interactions: [],
    next: 'LOCKING',
  },
  LOCKING: {
    animation: 'crt_signal_lock',
    interactions: [],
    next: 'REALM',
  },
  REALM: {
    interactions: ['knob', 'drop', 'transport'],
  },
  FAILED: {
    interactions: ['drop'],
  },
};

export const canInteract = (s: V1State, i: 'drop' | 'knob' | 'transport'): boolean =>
  STAGES[s].interactions.includes(i);
